import { useCallback, useState } from "react";
import { z } from "zod";
import {
  allowedTypesFor,
  INTAKE_UPLOAD_TYPES,
  PLAN_UPLOAD_TYPES,
  type UploadRole,
} from "@/schemas/plan-audit-upload";

/**
 * Getting the plan and its intake into storage, before anything is filed.
 *
 * Three steps per file: ask for a presigned URL, PUT the bytes straight to S3,
 * then confirm. The bytes never pass through the app server — that is the
 * documented posture for arbitrary documents (`.claude/rules/s3.md`).
 *
 * **This is the part that is not transactional and cannot be.** A dropped
 * connection, a proxy that answers 200 for a PUT it never delivered, a user who
 * retries after a timeout that actually succeeded — all of it happens here,
 * before the single transaction that files the run. So this hook reports per-file
 * state honestly and never decides on the caller's behalf what a partial result
 * means; `NewAuditView` refuses to file an incomplete intake, and the server
 * re-checks every file anyway.
 *
 * No percentage. `fetch` cannot report upload progress (that needs
 * XMLHttpRequest), and a percentage derived from files-done/files-total would be
 * a number that looks precise and isn't. Per-file states are the honest signal.
 */

export type UploadState = "waiting" | "uploading" | "uploaded" | "failed";

export interface TrackedUpload {
  /** Stable within one submission, so a retry updates rather than appends. */
  key: string;
  file: File;
  role: UploadRole;
  state: UploadState;
  /** The `file` row's id, once presigned. What the create-run request carries. */
  fileId?: string;
  error?: string;
}

export interface UploadOutcome {
  uploaded: TrackedUpload[];
  failed: TrackedUpload[];
}

/**
 * The content type for a file the user picked.
 *
 * Browsers routinely report `""` for `.md` and `.vtt` — there is no registered
 * type in most OS tables — and the server refuses a blank type rather than
 * storing something it cannot identify. So the extension decides, and the
 * browser's guess is only a fallback.
 */
export function contentTypeFor(file: File, role: UploadRole): string {
  const extension = file.name.toLowerCase().split(".").pop() ?? "";
  const byExtension: Record<string, string> = {
    md: "text/markdown",
    markdown: "text/markdown",
    txt: "text/plain",
    pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    vtt: "text/vtt",
  };
  const mapped = byExtension[extension];
  if (mapped !== undefined) return mapped;
  // Only trust the browser if what it says is something this role accepts.
  return allowedTypesFor(role).includes(file.type) ? file.type : "";
}

/** The `accept` attribute for a file input, derived from the same allowlist. */
export function acceptFor(role: UploadRole): string {
  const extensions =
    role === "plan" ? ".md,.markdown,.txt,.pdf,.docx" : ".md,.markdown,.txt,.pdf,.docx,.vtt";
  const types = role === "plan" ? PLAN_UPLOAD_TYPES : INTAKE_UPLOAD_TYPES;
  return [extensions, ...types].join(",");
}

export function uploadKey(file: File, role: UploadRole, index: number): string {
  return `${role}-${index}-${file.name}-${file.size}`;
}

/**
 * What `POST /api/plan-files` answers with. Parsed, not cast: this is a data
 * boundary, and `.claude/rules/code-quality.md` bans an `as` here — a shape that
 * quietly changed would otherwise surface as `undefined` in a PUT URL.
 */
const presignResponseSchema = z.object({
  id: z.string().min(1),
  url: z.string().min(1),
});

/**
 * One file, all three steps. Returns a new tracked row rather than throwing, so
 * the caller can show which file failed and offer to retry just that one.
 */
async function uploadOne(item: TrackedUpload): Promise<TrackedUpload> {
  const contentType = contentTypeFor(item.file, item.role);
  if (contentType.length === 0) {
    return { ...item, state: "failed", error: "That file type is not accepted." };
  }

  const fail = (error: string): TrackedUpload => ({ ...item, state: "failed", error });
  const serverMessage = async (res: Response, fallback: string): Promise<string> =>
    (await res
      .json()
      .then((json: { error?: string }) => json.error)
      .catch(() => null)) ?? fallback;

  try {
    // 1. Ask for a presigned PUT. This also creates the `file` row, pending.
    const presignRes = await fetch("/api/plan-files", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        role: item.role,
        filename: item.file.name,
        contentType,
        sizeBytes: item.file.size,
      }),
    });
    if (!presignRes.ok) {
      return fail(await serverMessage(presignRes, "Could not start the upload."));
    }
    const presigned = presignResponseSchema.safeParse(await presignRes.json().catch(() => null));
    if (!presigned.success) return fail("The upload could not be started.");
    const { id, url } = presigned.data;

    // 2. The bytes go straight to storage. Content-Type must match what the URL
    //    was signed for, or S3 rejects the signature.
    const putRes = await fetch(url, {
      method: "PUT",
      body: item.file,
      headers: { "Content-Type": contentType },
    });
    if (!putRes.ok) {
      // The `file` row stays `pending`, which is exactly what stops it being
      // attached to a plan version later.
      return { ...fail("The file did not reach storage."), fileId: id };
    }

    // 3. Mark it uploaded. Until this lands the server will refuse to file it.
    const confirmRes = await fetch("/api/files/confirm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    if (!confirmRes.ok) {
      return {
        ...fail(await serverMessage(confirmRes, "Could not confirm the upload.")),
        fileId: id,
      };
    }

    return { ...item, fileId: id, state: "uploaded" };
  } catch {
    // A network failure mid-PUT lands here. Nothing is filed, and the pending
    // row is harmless — it can never be attached.
    return fail("The upload did not go through.");
  }
}

export function useUploadAuditFiles() {
  const [uploads, setUploads] = useState<TrackedUpload[]>([]);

  const reset = useCallback(() => setUploads([]), []);

  /**
   * Uploads each file in turn and returns what succeeded and what did not.
   *
   * Sequential rather than `Promise.all`: at most twenty-one small documents, and
   * one-at-a-time gives a progress list a person can read and a failure they can
   * attribute. Parallel would finish marginally sooner and report "something
   * broke".
   *
   * Never throws on a per-file failure — the caller decides what a partial
   * result means, and for an intake the answer is "do not file the run".
   */
  const upload = useCallback(async (items: TrackedUpload[]): Promise<UploadOutcome> => {
    setUploads(items.map((item) => ({ ...item, state: "waiting" as const })));
    const done: TrackedUpload[] = [];

    for (const item of items) {
      setUploads((prev) =>
        prev.map((row) => (row.key === item.key ? { ...row, state: "uploading" } : row)),
      );
      const result = await uploadOne(item);
      done.push(result);
      setUploads((prev) => prev.map((row) => (row.key === result.key ? result : row)));
    }

    return {
      uploaded: done.filter((row) => row.state === "uploaded"),
      failed: done.filter((row) => row.state !== "uploaded"),
    };
  }, []);

  return { uploads, upload, reset };
}
